// src/components/reporting/cohort/CohortThemesPage.js
// Cohort Themes — what a cohort's case studies are about, with a Scarlett
// discussion beside it. SuperAdmin/SuperContentManager only.
//
//   status  GET  /api/v1/analytics/cohort-themes/status?cohortId=&targetTenantId=
//   create  POST /api/v1/analytics/cohort-themes            { cohort_id, targetTenantId }
//   fetch   GET  /api/v1/analytics/cohort-themes/:reportId?targetTenantId=
//   chat    POST /api/v1/analytics/cohort-themes/:cohortId/chat   (via CohortChatPanel)
//
// Same state machine, polling schedule, and split workspace as
// CohortSummaryPage: 'loading' | 'access-denied' | 'gate' | 'generating' |
// 'loading-report' | 'report' | 'error', with 'gate' skipped for CURRENT.
// Generation is only ever started by the Generate/Update button — never on
// selection or on load.
//
// The page chrome (StatusGate shape, GeneratingOverlay, the resize handle) is
// a copy of CohortSummaryPage's rather than a shared import, to leave that
// page untouched in this build. WorkspaceTabs isn't copied: one tab needs no
// strip. Extracting the shared pieces into cohort/workspace/ is a worthwhile
// follow-up now that there are two users.
//
// Category labels come from the report itself (result.taxonomyLabels), so a
// report always renders with the vocabulary it was generated under.
import { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { XCircleIcon, WarningCircleIcon, PrinterIcon } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import { useReportPolling } from '../../../hooks/useReportPolling';
import { useConversationRefresh } from '../../../hooks/useConversationRefresh';
import EditableTitle from '../../common/EditableTitle';
import CohortChatPanel from './CohortChatPanel';
import '../../../styles/cohortSummary.css';
import '../../../styles/cohortThemes.css';

const API_BASE = '/api/v1/analytics/cohort-themes';

const POLL_SCHEDULE = {
  initialDelayMs:   3_000,
  phase1DurationMs: 24 * 60 * 60 * 1000,
  phase1IntervalMs: 3_000,
  phase2IntervalMs: 3_000,
  totalTimeoutMs:   24 * 60 * 60 * 1000,
};

const SLOW_MESSAGE_AFTER_MS = 5 * 60_000;

const MIN_CHAT_PCT = 30;
const MAX_CHAT_PCT = 75;
const DEFAULT_CHAT_PCT = 55;

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString();
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// ---------------------------------------------------------------------------
// Gate
// ---------------------------------------------------------------------------

function CoverageLine({ coverage }) {
  return (
    <p className="cs-gate-text">
      {plural(coverage.qualifyingConversationCount, 'case study', 'case studies')} from{' '}
      {coverage.participantCount} of {coverage.totalCohortMembers} participants.
    </p>
  );
}

CoverageLine.propTypes = { coverage: PropTypes.object.isRequired };

function StatusGate({ statusData, onGenerate, onViewExisting }) {
  const { state, coverage, existingReport } = statusData;

  if (state === 'BELOW_THRESHOLD') {
    return (
      <div className="cs-gate-panel cs-gate-danger">
        <div className="cs-gate-icon"><XCircleIcon size={28} weight="fill" /></div>
        <div className="cs-gate-body">
          <h2 className="cs-gate-title">Not enough case studies yet</h2>
          <p className="cs-gate-text">
            A themes report needs at least {plural(coverage.threshold, 'completed case study', 'completed case studies')}.
            This cohort has {coverage.qualifyingConversationCount}.
          </p>
          {existingReport && (
            <button type="button" className="arp-btn arp-btn-secondary" onClick={() => onViewExisting(existingReport.id)}>
              View current report
            </button>
          )}
        </div>
      </div>
    );
  }

  if (state === 'STALE') {
    return (
      <div className="cs-gate-panel cs-gate-warning">
        <div className="cs-gate-icon"><WarningCircleIcon size={28} weight="fill" /></div>
        <div className="cs-gate-body">
          <h2 className="cs-gate-title">This report is out of date</h2>
          <p className="cs-gate-text">
            Case studies have been added or removed since it was generated on {formatDate(existingReport.createdAt)}.
          </p>
          <CoverageLine coverage={coverage} />
          <div className="cs-gate-actions">
            <button type="button" className="arp-btn arp-btn-primary" onClick={onGenerate}>
              Update themes report
            </button>
            <button type="button" className="arp-btn arp-btn-secondary" onClick={() => onViewExisting(existingReport.id)}>
              View current report
            </button>
          </div>
        </div>
      </div>
    );
  }

  // NO_REPORT_YET
  return (
    <div className="cs-gate-panel cs-gate-neutral">
      <div className="cs-gate-body">
        <h2 className="cs-gate-title">Ready to generate</h2>
        <CoverageLine coverage={coverage} />
        <button type="button" className="arp-btn arp-btn-primary" onClick={onGenerate}>
          Generate themes report
        </button>
      </div>
    </div>
  );
}

StatusGate.propTypes = {
  statusData: PropTypes.object.isRequired,
  onGenerate: PropTypes.func.isRequired,
  onViewExisting: PropTypes.func.isRequired,
};

function GeneratingOverlay({ estimatedTime }) {
  const [showSlowMessage, setShowSlowMessage] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowSlowMessage(true), SLOW_MESSAGE_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="cs-generating">
      <div className="loading-dots"><div className="loading-dot" /><div className="loading-dot" /><div className="loading-dot" /></div>
      <p className="cs-generating-text">Reading the case studies and finding themes…</p>
      {estimatedTime && !showSlowMessage && <p className="cs-generating-meta">Usually takes {estimatedTime}.</p>}
      {showSlowMessage && (
        <p className="cs-generating-meta cs-generating-slow">This is taking longer than usual, still working…</p>
      )}
    </div>
  );
}

GeneratingOverlay.propTypes = { estimatedTime: PropTypes.string };

// ---------------------------------------------------------------------------
// Report view
// ---------------------------------------------------------------------------

// Bar length by participants (breadth), with case-study count alongside —
// see the dictionary's "Conversations and participants" note for why.
function DualCountList({ distribution, labels, hideZero = true }) {
  if (!distribution || !labels) return null;
  const rows = Object.entries(distribution)
    .filter(([, v]) => !hideZero || v.conversations > 0)
    .sort((a, b) => b[1].participants - a[1].participants || b[1].conversations - a[1].conversations);
  if (rows.length === 0) return <p className="cs-detail-text-muted">None recorded.</p>;
  const max = Math.max(1, ...rows.map(([, v]) => v.participants));

  return (
    <div className="cs-driver-list">
      {rows.map(([key, v]) => (
        <div key={key} className="cs-driver-row ct-dual-row">
          <span className="cs-driver-label">{labels[key] || key}</span>
          <div className="cs-driver-track">
            <div className="cs-driver-fill" style={{ width: `${(v.participants / max) * 100}%` }} />
          </div>
          <span className="ct-dual-value">
            {plural(v.participants, 'person', 'people')}
            <span className="ct-dual-sub">{plural(v.conversations, 'case', 'cases')}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

DualCountList.propTypes = {
  distribution: PropTypes.object,
  labels: PropTypes.object,
  hideZero: PropTypes.bool,
};

function DistributionSection({ heading, subtext, distribution, labels }) {
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">{heading}</h3>
      {subtext && <p className="cs-detail-subtext">{subtext}</p>}
      <DualCountList distribution={distribution} labels={labels} />
    </div>
  );
}

DistributionSection.propTypes = {
  heading: PropTypes.string.isRequired,
  subtext: PropTypes.string,
  distribution: PropTypes.object,
  labels: PropTypes.object,
};

function ThemeCard({ theme, relationshipLabels }) {
  const who = (theme.topRelationships || [])
    .map((r) => (relationshipLabels?.[r.key] || r.key).toLowerCase())
    .join(', ');
  return (
    <article className="ct-theme">
      <header className="ct-theme-header">
        <h4 className="ct-theme-title">{theme.title}</h4>
        <span className="ct-theme-reach">
          {plural(theme.participantCount, 'person', 'people')}, {plural(theme.conversationCount, 'case study', 'case studies')}
        </span>
      </header>
      <p className="ct-theme-description">{theme.description}</p>
      {who && <p className="ct-theme-who">Mostly with: {who}</p>}
      {theme.excerpts?.length > 0 && (
        <div className="ct-theme-excerpts">
          {theme.excerpts.map((e, i) => (
            <blockquote key={i} className="ct-excerpt">{e}</blockquote>
          ))}
        </div>
      )}
    </article>
  );
}

ThemeCard.propTypes = {
  theme: PropTypes.object.isRequired,
  relationshipLabels: PropTypes.object,
};

function CommonPairings({ rows, labels }) {
  if (!rows?.length || !labels) return null;
  return (
    <div className="cs-detail-section">
      <h3 className="cs-detail-heading">Most common pairings</h3>
      <p className="cs-detail-subtext">Who the conversation is with, and what it is about.</p>
      <div className="ct-pairings">
        {rows.map((r) => (
          <div key={`${r.relationship}|${r.issueCategory}`} className="ct-pairing-row">
            <span className="ct-pairing-text">
              {labels.relationship[r.relationship] || r.relationship}
              <span className="ct-pairing-about"> about </span>
              {(labels.issueCategory[r.issueCategory] || r.issueCategory).toLowerCase()}
            </span>
            <span className="ct-pairing-count">
              {plural(r.participants, 'person', 'people')}, {plural(r.conversations, 'case', 'cases')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

CommonPairings.propTypes = {
  rows: PropTypes.array,
  labels: PropTypes.object,
};

function CoverageNote({ coverage }) {
  if (!coverage) return null;
  const { qualifyingConversationCount, participantCount, totalCohortMembers, withDebrief, byMethodology, tagged } = coverage;
  let text = Number.isInteger(totalCohortMembers)
    ? `${plural(qualifyingConversationCount, 'case study', 'case studies')} from ${participantCount} of ${totalCohortMembers} cohort members.`
    : `${plural(qualifyingConversationCount, 'case study', 'case studies')} from ${plural(participantCount, 'participant', 'participants')}.`;
  if (byMethodology) text += ` ${byMethodology.basic} scripted, ${byMethodology.real} role-play.`;
  if (Number.isInteger(withDebrief)) text += ` ${withDebrief} went on to a completed debrief.`;
  if (Number.isInteger(tagged) && tagged < qualifyingConversationCount) {
    text += ` ${qualifyingConversationCount - tagged} could not be analyzed and are left out of the counts below.`;
  }
  return <p className="cs-coverage-note">{text}</p>;
}

CoverageNote.propTypes = { coverage: PropTypes.object };

function ReportView({ report, cohortName }) {
  const result = report.result || {};
  const labels = result.taxonomyLabels;
  const d = result.distributions || {};

  return (
    <div className="cs-report">
      <div className="cs-report-header">
        <h2 className="cs-report-title">Themes: {cohortName || 'Cohort'}, {formatDate(report.createdAt)}</h2>
        <button type="button" className="rp-download-btn" onClick={() => window.print()} title="Print / save as PDF">
          <PrinterIcon size={18} />
        </button>
      </div>

      {result.narrative && (
        <div className="cs-narrative">
          {result.narrative.split('\n\n').filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}

      <CoverageNote coverage={result.coverage} />

      {result.themes?.length > 0 && (
        <section className="ct-themes">
          <h3 className="cs-detail-heading">Themes</h3>
          {result.themes.map((t) => (
            <ThemeCard key={t.id} theme={t} relationshipLabels={labels?.relationship} />
          ))}
        </section>
      )}

      <div className="cs-detail-divider" />
      <p className="cs-detail-intro">The categories behind these themes</p>

      <div className="cs-detail-grid">
        <DistributionSection
          heading="Who they are talking to"
          distribution={d.relationship}
          labels={labels?.relationship}
        />
        <DistributionSection
          heading="Inside or outside the organization"
          distribution={d.side}
          labels={labels?.side}
        />
        <DistributionSection
          heading="What the conversations are about"
          subtext="A case study can have up to two issues, so these add up to more than the total."
          distribution={d.issueCategory}
          labels={labels?.issueCategory}
        />
        <CommonPairings rows={result.relationshipByIssue} labels={labels} />
        <DistributionSection
          heading="What makes them hard"
          distribution={d.challengeType}
          labels={labels?.challengeType}
        />
        <DistributionSection
          heading="What is at stake"
          distribution={d.stakesType}
          labels={labels?.stakesType}
        />
        <DistributionSection
          heading="What they want from the conversation"
          distribution={d.desiredOutcome}
          labels={labels?.desiredOutcome}
        />
      </div>
    </div>
  );
}

ReportView.propTypes = {
  report: PropTypes.object.isRequired,
  cohortName: PropTypes.string,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CohortThemesPage = ({ cohortId, cohortName, targetTenantId, breadcrumb }) => {
  const { user } = useAuth();
  const { triggerRefresh } = useConversationRefresh();

  const [pageState, setPageState] = useState('loading');
  const [statusData, setStatusData] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [report, setReport] = useState(null);
  const [splitPct, setSplitPct] = useState(DEFAULT_CHAT_PCT);
  const [conversationId, setConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState(null);
  const workspaceRef = useRef(null);
  const isDragging = useRef(false);

  // The status response carries the cohort's name, so a page opened from the
  // conversation list (which may not know it) still titles the report.
  const displayCohortName = cohortName || statusData?.cohort?.name;
  const tenantQuery = `targetTenantId=${encodeURIComponent(String(targetTenantId))}`;

  const handleConversationResolved = useCallback((id, title) => {
    setConversationId(id);
    setConversationTitle(title);
  }, []);

  const handleTitleUpdate = useCallback(async (newTitle) => {
    try {
      await apiService.put(`/api/v1/conversations/${conversationId}`, { title: newTitle }, user);
      setConversationTitle(newTitle);
      triggerRefresh();
    } catch (err) {
      console.error('[CohortThemesPage] Failed to update conversation title:', err);
    }
  }, [conversationId, user, triggerRefresh]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    const onMouseMove = (moveEvent) => {
      if (!isDragging.current || !workspaceRef.current) return;
      const rect = workspaceRef.current.getBoundingClientRect();
      const pct = ((moveEvent.clientX - rect.left) / rect.width) * 100;
      setSplitPct(Math.min(Math.max(pct, MIN_CHAT_PCT), MAX_CHAT_PCT));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const openReport = useCallback(async (reportId) => {
    setPageState('loading-report');
    try {
      const resp = await apiService.get(`${API_BASE}/${reportId}?${tenantQuery}`, user);
      if (!resp.success || !resp.data) {
        setPageState('error');
        return;
      }
      if (resp.data.status === 'completed') {
        setReport(resp.data);
        setPageState('report');
      } else if (resp.data.status === 'failed') {
        setPageState('error');
      } else {
        setActiveReportId(reportId);
        setPageState('generating');
      }
    } catch (err) {
      console.error('[CohortThemesPage] Failed to load report:', err);
      setPageState('error');
    }
  }, [user, tenantQuery]);

  const loadStatus = useCallback(async () => {
    setPageState('loading');
    try {
      const resp = await apiService.get(
        `${API_BASE}/status?cohortId=${encodeURIComponent(cohortId)}&${tenantQuery}`,
        user
      );
      if (!resp.success || !resp.data) {
        setPageState('error');
        return;
      }
      setStatusData(resp.data);
      if (resp.data.state === 'CURRENT') {
        await openReport(resp.data.existingReport.id);
      } else {
        setPageState('gate');
      }
    } catch (err) {
      if (err?.status === 403 || err?.status === 404) {
        setPageState('access-denied');
        return;
      }
      console.error('[CohortThemesPage] Failed to load status:', err);
      setPageState('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, tenantQuery, openReport]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleGenerate = useCallback(async () => {
    try {
      const resp = await apiService.post(API_BASE, { cohort_id: cohortId, targetTenantId }, user);
      if (resp.success && resp.data?.reportId) {
        setEstimatedTime(resp.data.estimatedTime || null);
        setActiveReportId(resp.data.reportId);
        setPageState('generating');
      } else {
        setPageState('error');
      }
    } catch (err) {
      if (err?.code === 'THRESHOLD_NOT_MET') {
        await loadStatus();
        return;
      }
      console.error('[CohortThemesPage] Failed to start generation:', err);
      setPageState('error');
    }
  }, [cohortId, targetTenantId, user, loadStatus]);

  const fetchGeneratingStatus = useCallback(async (signal) => {
    try {
      const resp = await apiService.get(`${API_BASE}/${activeReportId}?${tenantQuery}`, user, { signal });
      if (!resp?.success || !resp.data) return null;
      return resp.data;
    } catch (err) {
      if (err?.name === 'AbortError') return null;
      if (err?.status >= 400 && err?.status < 500) {
        console.error('[CohortThemesPage] Poll failed with a client error, stopping:', err);
        return { status: 'failed' };
      }
      console.error('[CohortThemesPage] Poll failed, retrying:', err);
      return null;
    }
  }, [activeReportId, tenantQuery, user]);

  const handlePollUpdate = useCallback((polled) => {
    if (polled.status === 'completed') {
      setReport(polled);
      setPageState('report');
    } else if (polled.status === 'failed') {
      setPageState('error');
    }
  }, []);

  useReportPolling({
    active: pageState === 'generating' && !!activeReportId,
    pollKey: activeReportId,
    fetchStatus: fetchGeneratingStatus,
    onUpdate: handlePollUpdate,
    schedule: POLL_SCHEDULE,
  });

  return (
    <div className="cs-page">
      {breadcrumb}
      <div className="list-header cs-header">
        <div className="cs-header-left">
          {conversationId ? (
            <EditableTitle title={conversationTitle} conversationId={conversationId} onUpdate={handleTitleUpdate} />
          ) : (
            <h1 className="cs-static-title">Cohort Themes</h1>
          )}
        </div>
      </div>

      {pageState === 'loading' && <div className="arp-state">Loading…</div>}
      {pageState === 'loading-report' && <div className="arp-state">Loading report…</div>}

      {pageState === 'access-denied' && (
        <div className="arp-error-state">You don&apos;t have access to this cohort&apos;s themes report.</div>
      )}

      {pageState === 'gate' && statusData && (
        <StatusGate statusData={statusData} onGenerate={handleGenerate} onViewExisting={openReport} />
      )}

      {pageState === 'generating' && <GeneratingOverlay estimatedTime={estimatedTime} />}

      {pageState === 'report' && report && (
        <div className="cs-workspace" ref={workspaceRef}>
          <div className="cs-chat-panel" style={{ flexBasis: `${splitPct}%` }}>
            <CohortChatPanel
              cohortId={cohortId}
              entryPath={`${API_BASE}/${cohortId}/chat`}
              onConversationResolved={handleConversationResolved}
            />
          </div>
          <div className="cs-resize-handle" onMouseDown={handleResizeStart} />
          <div className="cs-tab-panel" style={{ flexBasis: `${100 - splitPct}%` }}>
            <div className="cs-tab-container">
              <div className="cs-tab-content">
                <ReportView report={report} cohortName={displayCohortName} />
              </div>
            </div>
          </div>
        </div>
      )}

      {pageState === 'error' && (
        <div className="cs-error-panel">
          <p className="arp-error-state">This report couldn&apos;t be generated. Try again, and if it fails again, check the server log for the report id.</p>
          <button type="button" className="arp-btn arp-btn-primary" onClick={loadStatus}>
            Try again
          </button>
        </div>
      )}
    </div>
  );
};

CohortThemesPage.propTypes = {
  cohortId: PropTypes.string.isRequired,
  cohortName: PropTypes.string,
  targetTenantId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  breadcrumb: PropTypes.node,
};

export default CohortThemesPage;